import { TmsStatePanel } from "@/components/tms/TmsStatePanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import * as React from "react";
import { useNavigate } from "react-router-dom";

type Load = {
  id: string;
  reference_number: string;
  status: string;
  coverage_type: string | null;
  origin_city: string;
  origin_state: string;
  dest_city: string;
  dest_state: string;
  pickup_window_start: string | null;
  customer_name: string | null;
  exception_count: string;
  driver_name: string | null;
  truck_unit: string | null;
  carrier_name: string | null;
};

const STATUS_TABS = ["all", "created", "covered", "dispatched", "in_transit", "delivered"] as const;

const STATUS_LABEL: Record<string, string> = {
  all: "All",
  created: "Created",
  covered: "Covered",
  dispatched: "Dispatched",
  in_transit: "In Transit",
  delivered: "Delivered",
};

const STATUS_BADGE: Record<string, string> = {
  created: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  covered: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  dispatched: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  in_transit: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  delivered: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const COVERAGE_BADGE: Record<string, string> = {
  own_fleet: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  preferred_carrier: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  spot_carrier: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
};

const COVERAGE_LABEL: Record<string, string> = {
  own_fleet: "Own Fleet",
  preferred_carrier: "Preferred",
  spot_carrier: "Spot",
};

function formatWindowDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function DispatchConsolePage() {
  const navigate = useNavigate();
  const [loads, setLoads] = React.useState<Load[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<string>("all");
  const [search, setSearch] = React.useState("");
  const deferredSearch = React.useDeferredValue(search.trim());

  const fetchLoads = React.useCallback(async (options?: { showLoader?: boolean }) => {
    if (options?.showLoader) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (activeTab !== "all") params.set("status", activeTab);
      if (deferredSearch) params.set("search", deferredSearch);
      const res = await fetch(`/api/tms/dispatch/loads?${params.toString()}`);
      const json = (await res.json()) as { status: string; data?: Load[]; error?: string };
      if (json.status === "success" && json.data) {
        setLoads(json.data);
      } else {
        setLoads([]);
        setError(json.error ?? "Failed to load dispatches.");
      }
    } catch (e) {
      setLoads([]);
      setError(e instanceof Error ? e.message : "Network error.");
    } finally {
      setLoading(false);
    }
  }, [activeTab, deferredSearch]);

  React.useEffect(() => {
    void fetchLoads({ showLoader: true });
  }, [fetchLoads]);

  // Auto-refresh every 30s
  React.useEffect(() => {
    const interval = setInterval(() => { void fetchLoads(); }, 30_000);
    return () => clearInterval(interval);
  }, [fetchLoads]);

  const filteredLoads = loads;
  const hasFilters = activeTab !== "all" || Boolean(search.trim());

  function resetFilters() {
    setActiveTab("all");
    setSearch("");
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Dispatch Console</h1>
          <p className="text-sm text-muted-foreground">Live view of all active loads</p>
        </div>
        <Button size="sm" onClick={() => navigate("/dispatch/loads/new")}>
          + New Load
        </Button>
      </div>

      {/* Summary row */}
      <DispatchSummary />

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {/* Status tabs */}
            <div className="flex gap-1 flex-wrap">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    activeTab === tab
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  {STATUS_LABEL[tab]}
                </button>
              ))}
            </div>
            {/* Search */}
            <input
              type="text"
              placeholder="Search loads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring w-full sm:w-56"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <TmsStatePanel
              tone="error"
              title="We couldn't load dispatch loads."
              description={error}
              actionLabel="Retry"
              onAction={() => void fetchLoads({ showLoader: true })}
            />
          ) : filteredLoads.length === 0 ? (
            <TmsStatePanel
              tone={hasFilters ? "filtered" : "empty"}
              title={hasFilters ? "No loads match your current filters." : "No active loads yet."}
              description={
                hasFilters
                  ? "Try another status or search term, or clear the filters to see every load."
                  : "Create your first load to start building the dispatch queue."
              }
              actionLabel={hasFilters ? "Clear filters" : "Create load"}
              onAction={hasFilters ? resetFilters : () => navigate("/dispatch/loads/new")}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4 font-medium">Reference</th>
                    <th className="pb-2 pr-4 font-medium">Route</th>
                    <th className="pb-2 pr-4 font-medium">Pickup</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 pr-4 font-medium">Coverage</th>
                    <th className="pb-2 pr-4 font-medium">Assigned To</th>
                    <th className="pb-2 font-medium">Exceptions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLoads.map((load) => (
                    <tr
                      key={load.id}
                      className="border-b last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/dispatch/loads/${load.id}`)}
                    >
                      <td className="py-3 pr-4">
                        <div className="font-medium">{load.reference_number}</div>
                        {load.customer_name ? (
                          <div className="text-xs text-muted-foreground">{load.customer_name}</div>
                        ) : null}
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground">
                        {load.origin_city}, {load.origin_state} → {load.dest_city}, {load.dest_state}
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground text-xs">
                        {formatWindowDate(load.pickup_window_start)}
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[load.status] ?? ""}`}>
                          {load.status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        {load.coverage_type ? (
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${COVERAGE_BADGE[load.coverage_type] ?? ""}`}>
                            {COVERAGE_LABEL[load.coverage_type] ?? load.coverage_type}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-xs text-muted-foreground">
                        {load.driver_name?.trim() || load.carrier_name || "—"}
                        {load.truck_unit ? <span className="ml-1 text-zinc-400">({load.truck_unit})</span> : null}
                      </td>
                      <td className="py-3">
                        {Number(load.exception_count) > 0 ? (
                          <span className="inline-flex items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-xs font-bold px-2 py-0.5 min-w-[20px]">
                            {load.exception_count}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DispatchSummary() {
  const [summary, setSummary] = React.useState<{
    total_active: number;
    at_risk: number;
    by_coverage_type: Record<string, number>;
    unresolved_exceptions: number;
  } | null>(null);

  React.useEffect(() => {
    fetch("/api/tms/dispatch/reports/summary")
      .then((r) => r.json())
      .then((j: { status: string; data?: typeof summary }) => {
        if (j.status === "success" && j.data) setSummary(j.data);
      })
      .catch(() => null);
  }, []);

  if (!summary) return null;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Card>
        <CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground">Active Loads</p>
          <p className="text-2xl font-bold">{summary.total_active}</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground">Own Fleet</p>
          <p className="text-2xl font-bold">{summary.by_coverage_type.own_fleet ?? 0}</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground">At Risk</p>
          <p className={`text-2xl font-bold ${summary.at_risk > 0 ? "text-red-500" : ""}`}>{summary.at_risk}</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground">Open Exceptions</p>
          <p className={`text-2xl font-bold ${summary.unresolved_exceptions > 0 ? "text-yellow-500" : ""}`}>{summary.unresolved_exceptions}</p>
        </CardContent>
      </Card>
    </div>
  );
}
